import { captureExceptionWithContext } from "@/sentry";
import { asyncPool } from "@/utils/asyncPool";

import { setTakenSprintIDs } from "./almHandler";

// Maximum concurrent sprint/iteration fetch calls to avoid backend rate-limiting (is 85 in the backend at time of writing)
const MAX_CONCURRENT_SPRINT_FETCHES = 30;

// Wire error codes from get_certificate_info -> short, transport-agnostic reasons the UI reads.
const CERT_READ_ERROR_REASONS = {
  ALMCONNECTION_COMMON__WRONG_PASSPHRASE_ERROR: "wrong_passphrase",
  ALMCONNECTION_COMMON__CERTIFICATE_UNREADABLE_ERROR: "unreadable",
};

/**
 * Common ALM connection-config handler (REN-15751, transport-aware).
 *
 * Connection scalars + per-type credentials route through `backend.almToolCall`
 * (NATS when the `useNats` flag + a registered adapter allow it, WAMP otherwise),
 * so the wire call stays `alm.{type}.{method}` on WAMP and hits the generated
 * stubs on NATS — see `almCommonAdapters.ts` for the registered set and the
 * documented carve-outs. Ops that still talk to `remote` directly (kept on WAMP)
 * are the ones whose response/payload shapes diverge from the stubs, that need
 * `callThrowing`'s success-envelope semantics, or that belong to the per-type
 * `almsync` clients owned by REN-15749 / REN-15750.
 */
export function almHandlerCommon(backend, type, remote, connectionId, almSessionId) {
  return {
    isAlm: !!connectionId,
    delete: () => {
      return backend.almToolCall(type, "delete", [], { connection_id: connectionId });
    },
    canConnect: () => {
      return backend.almToolCall(type, "can_connect", [], { connection_id: connectionId });
    },
    getAuthMethod: () => {
      return backend.almToolCall(type, "get_auth_method", [connectionId]);
    },
    setAuthMethod: (method) => {
      return backend.almToolCall(type, "set_auth_method", [connectionId, method]);
    },
    getName: () => {
      return backend.almToolCall(type, "get_name", [], { connection_id: connectionId });
    },
    setName: (name) => {
      return backend.almToolCall(type, "set_name", [], {
        name,
        connection_id: connectionId,
      });
    },
    getUrl: () => {
      return backend.almToolCall(type, "get_url", [], { connection_id: connectionId });
    },
    setUrl: (url) => {
      return backend.almToolCall(type, "set_url", [], {
        url,
        connection_id: connectionId,
      });
    },
    getUsername: () => {
      return backend.almToolCall(type, "get_username", [], { connection_id: connectionId });
    },
    setUsername: (username) => {
      return backend.almToolCall(type, "set_username", [], {
        username,
        connection_id: connectionId,
      });
    },
    getApiKey: () => {
      return backend.almToolCall(type, "get_apikey", [], { connection_id: connectionId });
    },
    setApiKey: (apiKey) => {
      return backend.almToolCall(type, "set_apikey", [], {
        apikey: apiKey,
        connection_id: connectionId,
      });
    },
    getTimeout: () => {
      return backend.almToolCall(type, "get_timeout", [], { connection_id: connectionId });
    },
    setTimeout: (timeout) => {
      return backend.almToolCall(type, "set_timeout", [], {
        timeout,
        connection_id: connectionId,
      });
    },
    getIgnoreWebhooksFiredByAlmUser: () => {
      return backend.almToolCall(type, "get_ignore_webhooks_fired_by_alm_user", [], {
        connection_id: connectionId,
      });
    },
    setIgnoreWebhooksFiredByAlmUser: (ignore) => {
      return backend.almToolCall(type, "set_ignore_webhooks_fired_by_alm_user", [], {
        connection_id: connectionId,
        ignore_webhooks_fired_by_alm_user: ignore,
      });
    },
    getUserMappingSettings: () => {
      return backend.almToolCall(type, "get_user_mapping_settings", [], {
        connection_id: connectionId,
      });
    },
    setUserMappingSettings: ({ auto, manual }) => {
      return backend.almToolCall(type, "set_user_mapping_settings", [], {
        auto,
        manual,
        connection_id: connectionId,
      });
    },
    // Route through the backend with `{ throwing: true }`: NATS raises on error
    // natively; the WAMP fallback uses callThrowing to keep the throw-on-
    // {success:false} semantic. The adapter unwraps the bool and renames the
    // request key (do_verify/disable_delete -> tlsVerification/disableDelete).
    getTlsVerification: () => {
      return backend.almToolCall(
        type,
        "get_tls_verification",
        [],
        { connection_id: connectionId },
        { throwing: true },
      );
    },
    setTlsVerification: (verify) => {
      return backend.almToolCall(
        type,
        "set_tls_verification",
        [],
        { do_verify: verify, connection_id: connectionId },
        { throwing: true },
      );
    },
    getDisableDelete: () => {
      return backend.almToolCall(
        type,
        "get_disable_delete",
        [],
        { connection_id: connectionId },
        { throwing: true },
      );
    },
    setDisableDelete: (del) => {
      return backend.almToolCall(
        type,
        "set_disable_delete",
        [],
        { disable_delete: del, connection_id: connectionId },
        { throwing: true },
      );
    },
    // `certificateType` is the certificate type ("ca"/"client"). The adapter
    // forwards it as the subject's certificate_id (they map 1:1). Both transports
    // converge on one of: `{ certificate_name, not_after }` (present + parseable),
    // `null` (no cert), or `{ error: <code> }` (unparseable). Normalize to the
    // transport-agnostic `{ name, notAfter, error }` the caller reads, mapping the
    // wire error code to a short reason.
    async getCertificateInfo(certificateType) {
      const res = await backend.almToolCall(type, "get_certificate_info", [], {
        type: certificateType,
        connection_id: connectionId,
      });
      if (!res) return { name: "", notAfter: null, error: null }; // no cert
      if (res.error) {
        return {
          name: "",
          notAfter: null,
          error: CERT_READ_ERROR_REASONS[res.error] ?? "unreadable",
        };
      }
      return { name: res.certificate_name, notAfter: res.not_after, error: null };
    },
    // set_certificate routes through the backend; the adapter maps the typed
    // CertificateTooLargeError back to the `{ success, error }` envelope the
    // consumer reads. `type` (cert type) renamed to avoid shadowing the ALM type.
    setCertificate: (data, name, certificateType) => {
      return backend.almToolCall(type, "set_certificate", [], {
        data,
        name,
        type: certificateType,
        connection_id: connectionId,
      });
    },
    deleteCertificate: (certificateType) => {
      return backend.almToolCall(type, "delete_certificate", [], {
        type: certificateType,
        connection_id: connectionId,
      });
    },
    // set_passphrase routes through the backend; the adapter maps the typed
    // WrongPassphraseError / CertificatePassphraseStorageError back to the
    // { success, errorCode, error } envelope the consumers read.
    setPassphrase: (passphrase) => {
      return backend.almToolCall(type, "set_passphrase", [], {
        passphrase,
        connection_id: connectionId,
      });
    },
    // almsync session ops via the transport-switching backend (NATS when the flag
    // + a registered almsync.{type} adapter allow it, WAMP otherwise). The WAMP
    // wire stays alm.{type}.session.X with identical positional args; the NATS
    // subject id is threaded via almSyncCall's sessionId arg where WAMP does not
    // carry it positionally (get_status, where it is a kwarg). set_alm_type stays
    // WAMP-only (callThrowing success-envelope semantics the backend does not expose).
    startSession: () => {
      return backend.almSyncCall(type, "session.start", [almSessionId]);
    },
    forceRunningSession: () => {
      return backend.almSyncCall(type, "session.force_running", [almSessionId]);
    },
    stopSession: () => {
      return backend.almSyncCall(type, "session.stop", [almSessionId]);
    },
    getStatus: () => {
      return backend.almSyncCall(
        type,
        "session.get_status",
        [],
        { session_id: almSessionId },
        almSessionId,
      );
    },
    setAlmType: (almType, artIds) => {
      // `{ throwing: true }`: NATS raises natively; the WAMP fallback keeps the
      // callThrowing throw-on-error semantic the caller's `.catch` relies on.
      return backend.almSyncCall(
        type,
        "session.set_alm_type",
        [almSessionId, almType, artIds],
        { connection_id: connectionId },
        almSessionId,
        { throwing: true },
      );
    },
    getSessionMapping: () => {
      return backend.almSyncCall(type, "session.get_mapping", [almSessionId]);
    },
    setSessionMapping: (mapping) => {
      return backend.almSyncCall(type, "session.set_mapping", [almSessionId, mapping]);
    },
    async getTeamProjects(teamIds) {
      const mapping = await this.getMapping();
      const project = {};
      teamIds.forEach((teamId) => {
        const m = mapping.find((m) => m.user_id === teamId);
        if (m) {
          m.sources.forEach((s) => {
            project[s.project_id] = s;
          });
        }
      });
      return Object.entries(project).map(([id, p]) => ({
        id: +id,
        name: p.name,
      }));
    },
    async getTeamsWithProjects(selectedTeams, teamsAndProjects, isCreating) {
      const fetchAvailableSprints = (project) => this.loadSourceSprints(project, isCreating);
      const selectedTeamIds = selectedTeams.map((team) => `${team.id || team.user_id}`);

      // Build a map of teamId -> projects from the ALM mapping
      const teamIdToProjects = {};
      teamsAndProjects
        .filter(({ user_id: teamId }) => selectedTeamIds.includes(`${teamId}`))
        .forEach(({ user_id, sources }) => {
          teamIdToProjects[user_id] = sources;
        });

      // Flatten all team-project combinations for controlled concurrent fetching
      const allProjectsToFetch = [];
      for (const team of selectedTeams) {
        const projects = teamIdToProjects[team.id] ?? [];
        for (const project of projects) {
          allProjectsToFetch.push({
            teamId: team.id,
            project: { ...project, sprint: null, sprints: [] },
          });
        }
      }

      // Fetch sprints with concurrency limit to avoid overloading backend/ALM
      await asyncPool(MAX_CONCURRENT_SPRINT_FETCHES, allProjectsToFetch, async ({ project }) => {
        try {
          await fetchAvailableSprints(project);
        } catch (error) {
          captureExceptionWithContext(error, {
            message: "Problem loading iterations",
            project,
          });
          throw error;
        }
      });

      // Reconstruct the team structure from fetched projects
      const teamIdToFetchedProjects = {};
      for (const { teamId, project } of allProjectsToFetch) {
        if (!teamIdToFetchedProjects[teamId]) {
          teamIdToFetchedProjects[teamId] = [];
        }
        teamIdToFetchedProjects[teamId].push(project);
      }

      const teamsWithProjects = selectedTeams.map((team) => {
        const projects = teamIdToFetchedProjects[team.id] ?? [];
        const takenSprintIDs = Object.fromEntries(projects.map(({ id }) => [id, []]));

        return {
          name: team.name,
          id: team.id,
          sources: projects,
          takenSprintIDs,
        };
      });
      teamsWithProjects.sort((a, b) => parseFloat(a.id) - parseFloat(b.id));
      return teamsWithProjects;
    },
    async addTeamsToIterations(iterations, teams, isCreating) {
      const teamToAlmProjectMap = await this.getMapping();
      const iterationsWithTeamsAndSprints = await this.getSessionMapping();

      // We take teams from the first iterations because they're the same in all iterations
      const teamIdsInIterations = iterationsWithTeamsAndSprints[0].teams.map(
        ({ team_id }) => `${team_id}`,
      );
      const iterationToTeamSprintsMap = iterationsWithTeamsAndSprints.reduce(
        iterationsToTeamSprintsMap,
        {},
      );
      const teamsInAlm = teams.filter((team) => teamIdsInIterations.includes(`${team.id}`));

      const teamsWithProjects = await this.getTeamsWithProjects(
        teamsInAlm,
        teamToAlmProjectMap,
        isCreating,
      );

      for (const iteration of iterations) {
        const teamToSprintMap = iterationToTeamSprintsMap[iteration.id];
        iteration.teams = JSON.parse(JSON.stringify(teamsWithProjects));
        for (const teamWithProjects of iteration.teams) {
          const projectToSprintMap = teamToSprintMap[teamWithProjects.id];
          for (const project of teamWithProjects.sources) {
            const sessionSource = projectToSprintMap[project.id] ?? {};
            await this.setSourceSprint(project, sessionSource);
          }
        }
      }

      // prepare available sprints options without already selected sprints, i.e. set taken sprints IDs array
      iterations.forEach((iteration) => {
        iteration.teams.forEach((team) => {
          team.sources.forEach((source) => setTakenSprintIDs(iterations, team.id, source.id));
        });
      });
    },
    // Mapping sources route through the backend. The adapter unwraps the stub's
    // `{ sources }` into the `{ data }` envelope consumers destructure, and wraps
    // the positional sources array into `{ sources }` on set. Jira overrides the
    // team variant with its formatted form (almHandlerJira), so the common team
    // method here serves ADO/AC; the art variant is type-agnostic.
    getTeamMappingSources(teamId) {
      return backend.almToolCall(type, "get_team_mapping_sources", [connectionId, +teamId]);
    },
    setTeamMappingSources(teamId, sources) {
      // Strip client-only row keys
      const payload = sources.map(({ _clientKey, ...rest }) => rest);
      return backend.almToolCall(type, "set_team_mapping_sources", [
        connectionId,
        +teamId,
        payload,
      ]);
    },
    getArtMappingSources(artId) {
      return backend.almToolCall(type, "get_art_mapping_sources", [connectionId, +artId]);
    },
    // TODO should probably be a backend function
    getArtsMappingSources(artIds) {
      const fetchSources = async (artId) => {
        const { data: sources } = await this.getArtMappingSources(artId);
        return {
          artId,
          projects: sources.map(({ project_id, name }) => ({
            id: project_id,
            name,
          })),
        };
      };
      return Promise.all(artIds.map(fetchSources));
    },
    setArtMappingSources(artId, sources) {
      // Strip client-only row keys
      const payload = sources.map(({ _clientKey, ...rest }) => rest);
      return backend.almToolCall(type, "set_art_mapping_sources", [connectionId, +artId, payload]);
    },
    checkIssueType() {
      return { success: true, data: [] };
    },
    getReleases() {
      return [];
    },
    async loadAlmItemTypes() {
      return await backend.almSyncCall(type, "item.load_alm_item_types", [connectionId]);
    },
    async searchAlmUsers(query) {
      return (
        await backend.almSyncCall(
          type,
          "session.search_alm_users",
          [connectionId, query],
          undefined,
          almSessionId,
        )
      ).map(mapUserMapping);
    },
    async getAlmUser() {
      return mapUserMapping(
        await backend.almSyncCall(type, "session.get_alm_user", [connectionId]),
      );
    },
    setAlmUser(almId) {
      return backend.almSyncCall(
        type,
        "session.set_alm_user",
        [connectionId, almId],
        undefined,
        almSessionId,
      );
    },
  };
}

function mapUserMapping(mapping) {
  return {
    almId: mapping?.alm_id,
    almName: mapping?.alm_name,
    pipId: mapping?.pip_id,
    pipName: "",
    error: mapping?.error,
  };
}

function projectToSprintMap(map, { id, ...sprint }) {
  return { ...map, [id]: sprint };
}

function teamToSprintsMap(map, { team_id, sources: sprints }) {
  return { ...map, [team_id]: sprints.reduce(projectToSprintMap, {}) };
}

function iterationsToTeamSprintsMap(map, { pi_iteration_id, teams }) {
  return {
    ...map,
    [pi_iteration_id]: teams.reduce(teamToSprintsMap, {}),
  };
}
